<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'FieldsetAddress',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    address: {
      type: Object,
      required: true,
    },
    labelAlign: {
      type: String,
      required: false,
      default: 'left',
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const addressTypes = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    addressTypes: addressTypes.value,
    ux: ux.value,
  }));

  const $$load_addressTypes = () => {
    return $app.value.repo.get('ui_metadata', { path: 'address_types' });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'addressTypes' }, { name: 'ux' }],
    loaders: { addressTypes: $$load_addressTypes },
    ux: {},
    dataRefs: { addressTypes, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.address,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('address');
    }
  );
  watch(
    () => props.labelAlign,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('labelAlign');
    }
  );

  const act_7315092382398562_reloadComponentData_v4ywf1blq =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_vouse1177 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_v4ywf1blq,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_vouse1177],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_vouse1177,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_reload_vouse1177 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_vouse1177.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div class="row">
      <ItpFormGroup
        name="address_type"
        :label="pgettext('Address', 'Address Type')"
        :labelAlign="labelAlign"
        required
        class="col-7"
      >
        <ItpFormNativeSelect
          name="address_type"
          v-model="address.address_type"
          :options="addressTypes"
          valueField="value"
          textField="name"
          required
        >
        </ItpFormNativeSelect>
      </ItpFormGroup>
      <ItpBox
        direction="column"
        alignItems="end"
        justifyContent="end"
        class="col-5 mb-4"
      >
        <ItpFormCheckbox
          name="is_default"
          class="fw-bold"
          :label="gettext('Default')"
          inline
          is-switch
          v-model="address.is_default"
          :readonly="address.isReadonly"
        >
        </ItpFormCheckbox>
      </ItpBox>
    </div>
    <hr />
    <div class="row">
      <ItpFormGroup
        name="country"
        :label="pgettext('Address', 'Country')"
        :labelAlign="labelAlign"
        class="col-7"
      >
        <ItpFormInput name="country" v-model="address.country"> </ItpFormInput>
      </ItpFormGroup>
      <ItpFormGroup
        name="region"
        :label="pgettext('Address', 'Region')"
        :labelAlign="labelAlign"
        class="col-5"
      >
        <ItpFormInput name="region" v-model="address.region"> </ItpFormInput>
      </ItpFormGroup>
    </div>
    <div class="row">
      <ItpFormGroup
        name="postal_code"
        :label="pgettext('Address', 'Postal Code')"
        :labelAlign="labelAlign"
        class="col-3"
      >
        <ItpFormInput name="postal_code" v-model="address.postal_code">
        </ItpFormInput>
      </ItpFormGroup>
      <ItpFormGroup
        name="city"
        :label="pgettext('Address', 'City')"
        :labelAlign="labelAlign"
        class="col-6"
      >
        <ItpFormInput name="city" v-model="address.city"> </ItpFormInput>
      </ItpFormGroup>
      <ItpFormGroup
        name="district"
        :label="pgettext('Address', 'District')"
        :labelAlign="labelAlign"
        class="col-3"
      >
        <ItpFormInput name="district" v-model="address.district">
        </ItpFormInput>
      </ItpFormGroup>
    </div>
    <hr />
    <div class="row">
      <ItpFormGroup
        name="public_place"
        :label="pgettext('Address', 'Public Place')"
        :labelAlign="labelAlign"
        class="col-7"
      >
        <ItpFormInput name="public_place" v-model="address.public_place">
        </ItpFormInput>
      </ItpFormGroup>
      <ItpFormGroup
        name="public_place_type"
        :label="pgettext('Address', 'Public Place Type')"
        :labelAlign="labelAlign"
        class="col-5"
      >
        <ItpFormInput
          name="public_place_type"
          v-model="address.public_place_type"
        >
        </ItpFormInput>
      </ItpFormGroup>
    </div>
    <div class="row">
      <ItpFormGroup
        name="house_nr"
        :label="pgettext('Address', 'House Nr')"
        :labelAlign="labelAlign"
        class="col"
      >
        <ItpFormInput name="house_nr" v-model="address.house_nr">
        </ItpFormInput>
      </ItpFormGroup>
      <ItpFormGroup
        name="building_nr"
        :label="pgettext('Address', 'Building Nr')"
        :labelAlign="labelAlign"
        class="col"
      >
        <ItpFormInput name="building_nr" v-model="address.building_nr">
        </ItpFormInput>
      </ItpFormGroup>
      <ItpFormGroup
        name="sub_building_nr"
        :label="pgettext('Address', 'Sub Building Nr')"
        :labelAlign="labelAlign"
        class="col"
      >
        <ItpFormInput name="sub_building_nr" v-model="address.sub_building_nr">
        </ItpFormInput>
      </ItpFormGroup>
      <ItpFormGroup
        name="floor_nr"
        :label="pgettext('Address', 'Floor Nr')"
        :labelAlign="labelAlign"
        class="col"
      >
        <ItpFormInput name="floor_nr" v-model="address.floor_nr">
        </ItpFormInput>
      </ItpFormGroup>
      <ItpFormGroup
        name="door_nr"
        :label="pgettext('Address', 'Door Nr')"
        :labelAlign="labelAlign"
        class="col"
      >
        <ItpFormInput name="door_nr" v-model="address.door_nr"> </ItpFormInput>
      </ItpFormGroup>
    </div>
    <hr />
    <ItpFormGroup
      name="other_details"
      :label="pgettext('Address', 'Other Details')"
      :labelAlign="labelAlign"
    >
      <ItpFormTextarea name="other_details" v-model="address.other_details">
      </ItpFormTextarea>
    </ItpFormGroup>
  </ItpUsercontrol>
</template>
